import Row from "@amzn/meridian/esm/components/row";
import Text from "@amzn/meridian/esm/components/text";
import Theme from "@amzn/meridian/esm/components/theme";
import Tooltip from "@amzn/meridian/esm/components/tooltip";
import chevronDownTokens from "@amzn/meridian-tokens/base/icon/chevron-down-small";
import chevronUpTokens from "@amzn/meridian-tokens/base/icon/chevron-up-small";
import pencilTokens from "@amzn/meridian-tokens/base/icon/pencil";
import trashTokens from "@amzn/meridian-tokens/base/icon/trash";
import { addedRowHighlightClass } from "../../../styles/motion";
import { gridStackClass } from "../insights.styles";
import DeleteConfirmButtons from "../DeleteConfirmButtons";
import HeightReserver from "../HeightReserver";
import { recapToggleClass, rowTitleClass, strikethroughClass } from "./agenda.styles";
import IconButton from "../IconButton";
import { useHoverReveal, useScrollDismissedTooltip } from "../insights.hooks";
import { AGENDA_STATE, IN_PROGRESS_LABEL, IN_PROGRESS_TITLE_TOKENS, LABEL_ROW_WIDTHS, ROW_LINE_HEIGHT, TITLE_TRUNCATE_LENGTH } from "./agenda.constants";
import { DELETE_CONFIRM_LABEL, deleteButtonTheme } from "../insights.constants";
import { truncateItemText } from "../insights.utils";

// Title text — shared between DisplayRow and ConfirmDeleteRow so height parity is guaranteed.
const TitleContent = ({ item }) => {
  const isCompleted = item.state === AGENDA_STATE.COMPLETED;
  const isInProgress = item.state === AGENDA_STATE.IN_PROGRESS;
  const { display, isTruncated } = truncateItemText(item.title, TITLE_TRUNCATE_LENGTH);
  const { open, anchorProps } = useScrollDismissedTooltip();

  // Always render the Tooltip and gate `open`, so crossing the threshold doesn't remount the anchor.
  return (
    <Tooltip open={isTruncated && open} position="top" title={item.title}>
      <div className={rowTitleClass} {...anchorProps}>
        {isInProgress ? (
          <Theme tokens={IN_PROGRESS_TITLE_TOKENS}>
            <Text type="b200">{display}</Text>
          </Theme>
        ) : (
          <Text
            type="b200"
            className={isCompleted ? strikethroughClass : undefined}
            color={isCompleted ? "secondary" : "primary"}
          >
            {display}
          </Text>
        )}
      </div>
    </Tooltip>
  );
};

// Display state: title + edit/delete controls (hover-revealed) + optional "In progress" label.
// The controls and "In progress" label crossfade in the same space via gridStackClass.
const DisplayRow = ({
  item, onEdit, onDelete, controlsStyle, restingStyle, spacingInset,
  expanded, onToggleRecap,
}) => {
  const isCompleted = item.state === AGENDA_STATE.COMPLETED;
  const isInProgress = item.state === AGENDA_STATE.IN_PROGRESS;

  // Meridian's Expander is one tag holding both the arrow and the panel, but those belong in
  // different props here — arrow in the step's `label`, panel in its `children` — so it can't be used.
  //
  // Clicking the title toggles too, as a larger mouse target. The chevron is the only focusable
  // control, so keyboard users get one tab stop per row rather than two for the same action.
  const title = isCompleted ? (
    <div className={recapToggleClass} onClick={onToggleRecap}>
      <TitleContent item={item} />
    </div>
  ) : (
    <TitleContent item={item} />
  );

  return (
    <Row width="100%" widths={LABEL_ROW_WIDTHS} spacing="200" alignmentVertical="top" spacingInset={spacingInset}>
      {title}
      {isCompleted ? (
        // No edit/delete on a completed item, so nothing crossfades into this slot and the chevron
        // can live here permanently — right-aligned, so it lines up with the "In progress" label
        // on the rows above and below.
        <Row height={ROW_LINE_HEIGHT} spacing="200" alignmentVertical="center" alignmentHorizontal="end">
          <IconButton
            icon={expanded ? chevronUpTokens : chevronDownTokens}
            onClick={onToggleRecap}
            ariaLabel={expanded ? "Hide recap" : "Show recap"}
          />
        </Row>
      ) : (
        <div className={gridStackClass}>
          <Row height={ROW_LINE_HEIGHT} spacing="200" alignmentVertical="center" alignmentHorizontal="end">
            <IconButton icon={pencilTokens} onClick={onEdit} ariaLabel="Edit" style={controlsStyle} />
            <IconButton icon={trashTokens} onClick={onDelete} ariaLabel="Delete" tokens={deleteButtonTheme} style={controlsStyle} />
          </Row>
          {isInProgress && (
            <div style={restingStyle}>
              <Row height={ROW_LINE_HEIGHT} alignmentVertical="center" alignmentHorizontal="end">
                <Text type="b100" color="secondary">{IN_PROGRESS_LABEL}</Text>
              </Row>
            </div>
          )}
        </div>
      )}
    </Row>
  );
};

// Delete-confirm state: hidden title reserves height, prompt overlays it. Buttons are direct
// Row children — no overlay needed (matches ActionItemCard's ConfirmDeleteRow).
const ConfirmDeleteRow = ({ item, onConfirmDelete, onCancelDelete, spacingInset }) => (
  <Row width="100%" widths={LABEL_ROW_WIDTHS} spacing="200" alignmentVertical="top" spacingInset={spacingInset}>
    <div className={gridStackClass}>
      <HeightReserver>
        <TitleContent item={item} />
      </HeightReserver>
      <Row alignmentVertical="top" spacing="none">
        <Text type="b200" color="secondary">{DELETE_CONFIRM_LABEL}</Text>
      </Row>
    </div>
    <Row height={ROW_LINE_HEIGHT} spacing="200" alignmentVertical="center" alignmentHorizontal="end">
      <DeleteConfirmButtons onConfirm={onConfirmDelete} onCancel={onCancelDelete} />
    </Row>
  </Row>
);

/**
 * AgendaRowLabel - Non-editing label for a ProgressTracker step.
 * A `confirming` fork picks the whole row: ConfirmDeleteRow vs DisplayRow.
 * Both share TitleContent for height parity (matches ActionItemCard's pattern).
 */
const AgendaRowLabel = ({
  item, onEdit, onDelete, onConfirmDelete, onCancelDelete, confirming, highlighted, spacingInset,
  expanded, onToggleRecap,
}) => {
  const { bind, controlsStyle, restingStyle } = useHoverReveal();

  return (
    <div style={{ width: "100%" }} {...bind}>
      <div className={highlighted ? addedRowHighlightClass : undefined}>
        {confirming ? (
          <ConfirmDeleteRow item={item} onConfirmDelete={onConfirmDelete} onCancelDelete={onCancelDelete} spacingInset={spacingInset} />
        ) : (
          <DisplayRow
            item={item}
            onEdit={onEdit}
            onDelete={onDelete}
            controlsStyle={controlsStyle}
            restingStyle={restingStyle}
            spacingInset={spacingInset}
            expanded={expanded}
            onToggleRecap={onToggleRecap}
          />
        )}
      </div>
    </div>
  );
};

export default AgendaRowLabel;
